import React, { FC, memo, useCallback, useContext, useMemo } from 'react';
import { useLegoElementError, useLegoElementValue } from '../../hooks';
import { TLegoElementProps } from '../../models';
import { LegoV2Context } from '../../context';
import { Select } from '@src/atoms/Select';
import { AnyType } from '@src/models/common';
import { findCurrValue, findCurrValueList } from './utils';
import { getErrorsMessage } from '../../utils';

export const LegoElementSelect: FC<
  TLegoElementProps & Omit<Parameters<typeof Select>[0], 'id' | 'onChange' | 'isHelpMessage' | 'errorMessage'>
> = memo(({ id, ...props }) => {
  const { value, onChange } = useLegoElementValue(id);
  const { validate } = useContext(LegoV2Context);
  const { errorMessage } = useLegoElementError(id);

  const handleChange = useCallback(
    (v: AnyType) => {
      const value = props.isMulti ? v?.map((vv) => vv.value) : v?.value;

      onChange(id, value);
      validate(id, value);
    },
    [id, onChange, props.isMulti, validate],
  );

  const handleNoOptionsMessage = useCallback(() => {
    // Когда в селекте нечего выводить в список в дропдаун - в него выводится это сообщение
    return 'Список пустой';
  }, []);

  // Считаем выбранное значение в селекте
  const selectValue = useMemo(
    () =>
      props.isMulti
        ? findCurrValueList(props.options as AnyType[], value)
        : findCurrValue(props.options as AnyType[], value),
    [props.isMulti, props.options, value],
  );

  return (
    <Select
      noOptionsMessage={handleNoOptionsMessage}
      {...props}
      id={id}
      value={selectValue}
      isHelpMessage
      onChange={handleChange}
      errorMessage={getErrorsMessage(errorMessage)}
    />
  );
});
